import React, { useEffect, useState } from "react";
import { Search, Heart, ShoppingCart, User, Sliders, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import WhatsAppButton from "./WhatsAppButton";
import Footer from "./Footer";
import Header from "./Header";
import AddToCartButton from "./AddToCart";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { getUserId } from "../utils/utils";

const sampleProducts = [
  {
    id: 1,
    name: "Elegant Silver Solitaire Ring",
    category: "Rings",
    price: 129.99,
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcShCqMk8g638gCFHEGOTrUBEOjrnYTJIQiVhg&s",
  },
  {
    id: 2,
    name: "Minimalist Silver Pendant Necklace",
    category: "Necklaces",
    price: 89.99,
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSXS1FClPSs-EQgx6ivuIxfOUvcIEUfJHS4cw&s",
  },
  {
    id: 3,
    name: "Silver Hoop Earrings",
    category: "Earrings",
    price: 69.99,
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQb0mOk4fEFcnSIghmXM5b2EQwqkJfqRjJVLw&s",
  },
  {
    id: 4,
    name: "Classic Silver Bangle Bracelet",
    category: "Bracelets",
    price: 99.99,
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRni62bkp1qxNUN0b51XLCXoyaz6z9MIeiuPQ&s",
  },
  {
    id: 5,
    name: "Puja Silver Article Set",
    category: "Articles",
    price: 199.99,
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRpS5cIkRLcN9VTKYqBj6xI0SgmS5zPb6wPJw&s",
  },
];
const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [priceRange, setPriceRange] = useState([0, 200]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState({
    categories: true,
    products: true,
  });
  const userId = getUserId();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  const filteredProducts = sampleProducts.filter((product) => {
    const matchesSearch = product.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "" || product.category === selectedCategory;
    const matchesPrice =
      product.price >= priceRange[0] && product.price <= priceRange[1];
    return matchesSearch && matchesCategory && matchesPrice;
  });
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesSnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = [];
        categoriesSnapshot.forEach((doc) => {
          categoriesData.push({ id: doc.id, ...doc.data() });
        });
        setCategories(categoriesData);
        setLoading((prev) => ({ ...prev, categories: false }));

        // Fetch products
        const productsSnapshot = await getDocs(collection(db, "products"));
        const productsData = [];
        productsSnapshot.forEach((doc) => {
          productsData.push({ id: doc.id, ...doc.data() });
        });
        setProducts(productsData);
        setLoading((prev) => ({ ...prev, products: false }));
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle error appropriately
      }
    };

    fetchData();
  }, []);
  const addToCart = async (product) => {
    try {
      const cartRef = collection(db, `users/${userId}/cart`);
      await addDoc(cartRef, { ...product, quantity: 1 });
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };

  useEffect(() => {
    // Flag to track if the component is mounted
    let isMounted = true;

    // Separate async function
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const q = query(
          productsRef,
          where("category.name", "==", selectedCategory)
        );

        const productsSnapshot = await getDocs(q);
        const productsData = [];

        // Check if component is still mounted before updating state
        if (isMounted) {
          productsSnapshot.forEach((doc) => {
            productsData.push({ id: doc.id, ...doc.data() });
          });
          setProducts(productsData);
          setLoading((prev) => ({ ...prev, products: false }));
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        if (isMounted) {
          setLoading((prev) => ({ ...prev, products: false }));
        }
      }
    };
    const fetchProductsAll = async () => {
      try {
        const productsRef = collection(db, "products");

        const productsSnapshot = await getDocs(productsRef);
        const productsData = [];

        // Check if component is still mounted before updating state
        if (isMounted) {
          productsSnapshot.forEach((doc) => {
            productsData.push({ id: doc.id, ...doc.data() });
          });
          setProducts(productsData);
          setLoading((prev) => ({ ...prev, products: false }));
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        if (isMounted) {
          setLoading((prev) => ({ ...prev, products: false }));
        }
      }
    };

    // Call the async function
    if (selectedCategory === "" || selectedCategory === "All") {
      fetchProductsAll();
    } else {
      fetchProducts();
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [selectedCategory]);
  return (
    <div className="min-h-screen flex flex-col bg-purple-50">
      <Header />

      {/* Main Content */}
      <div className="container mx-auto py-4 px-4 md:py-8 md:px-6">
        {/* Search Header */}
        <div className="mb-4 md:mb-8">
          <div className="flex items-center gap-2 md:gap-4 mb-6">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search for jewelry..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 md:px-6 md:py-4 pr-12 bg-white rounded-lg md:rounded-xl shadow-sm border border-purple-100 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all text-sm md:text-base"
              />
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-purple-400 w-5 h-5 md:w-6 md:h-6" />
            </div>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-3 md:p-4 bg-white rounded-lg md:rounded-xl shadow-sm border border-purple-100 hover:bg-purple-50 transition-all md:hidden"
            >
              <Sliders className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          {/* Mobile Sidebar Overlay */}
          {isSidebarOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={() => setIsSidebarOpen(false)} />
          )}

          {/* Sidebar */}
          <aside 
            className={`
              fixed md:static inset-y-0 right-0 w-72 md:w-64 
              transform transition-transform duration-300 ease-in-out 
              ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
              md:block z-50 md:z-0
            `}
          >
            <div className="h-full md:h-auto bg-white rounded-l-xl md:rounded-xl p-6 border border-purple-100 md:shadow-none">
              <div className="flex justify-between items-center mb-6 md:hidden">
                <h3 className="text-xl font-serif text-purple-900">Filters</h3>
                <button onClick={() => setIsSidebarOpen(false)} className="p-2">
                  <X className="w-6 h-6 text-purple-600" />
                </button>
              </div>

              <h3 className="hidden md:block text-xl font-serif mb-6 text-purple-900">Filters</h3>

              {/* Categories */}
              <div className="mb-8">
                <h4 className="text-sm font-medium text-purple-600 mb-4">Categories</h4>
                <div className="space-y-2">
                  {[{name:"All"}, ...categories].map((cat) => (
                    <button
                      key={cat?.name}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                        selectedCategory === cat?.name
                          ? "bg-purple-900 text-white"
                          : "text-purple-700 hover:bg-purple-50"
                      }`}
                      onClick={() => {
                        setSelectedCategory(selectedCategory === cat?.name ? "" : cat?.name);
                        setIsSidebarOpen(false);
                      }}
                    >
                      {cat?.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <h4 className="text-sm font-medium text-purple-600 mb-4">Price Range</h4>
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="10"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                  className="w-full accent-purple-900"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-sm text-purple-600">₹{priceRange[0]}</span>
                  <span className="text-sm text-purple-600">₹{priceRange[1]}</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-white rounded-lg md:rounded-xl overflow-hidden shadow-sm border border-purple-100 hover:shadow-lg transition-all duration-300"
                    onClick={() => navigate("/product-details", { state: { product } })}
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={product?.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <button className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <Heart className="w-5 h-5 text-purple-600 hover:text-red-500 transition-colors" />
                      </button>
                    </div>
                    <div className="p-4 md:p-6">
                      <h4 className="text-base md:text-lg font-medium text-purple-900 mb-2">
                        {product.name}
                      </h4>
                      <p className="text-lg md:text-xl font-serif text-purple-800 mb-4">
                        ₹{product.price}
                      </p>
                      <AddToCartButton
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(product);
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 md:py-12">
                <p className="text-lg md:text-xl text-purple-600">
                  No products found matching your criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <WhatsAppButton />
      <Footer />
    </div>
    // <div className="min-h-screen flex flex-col bg-purple-50">
    //   <Header />

    //   {/* Main Content */}
    //   <div className="container mx-auto py-8 px-6">
    //     {/* Search Header */}
    //     <div className="mb-8">
    //       <div className="flex items-center gap-4 mb-6">
    //         <div className="flex-1 relative">
    //           <input
    //             type="text"
    //             placeholder="Search for jewelry..."
    //             value={searchQuery}
    //             onChange={(e) => setSearchQuery(e.target.value)}
    //             className="w-full px-6 py-4 pr-12 bg-white rounded-xl shadow-sm border border-purple-100 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
    //           />
    //           <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-purple-400" />
    //         </div>
    //         <button
    //           onClick={() => setIsSidebarOpen(!isSidebarOpen)}
    //           className="p-4 bg-white rounded-xl shadow-sm border border-purple-100 hover:bg-purple-50 transition-all"
    //         >
    //           <Sliders className="w-6 h-6 text-purple-600" />
    //         </button>
    //       </div>
    //     </div>

    //     <div className="flex gap-8">
    //       {/* Sidebar */}
    //       {isSidebarOpen && (
    //         <aside className="w-72 shrink-0">
    //           <div className="bg-white rounded-xl shadow-sm p-6 border border-purple-100">
    //             <h3 className="text-xl font-serif mb-6 text-purple-900">
    //               Filters
    //             </h3>

    //             {/* Categories */}
    //             <div className="mb-8">
    //               <h4 className="text-sm font-medium text-purple-600 mb-4">
    //                 Categories
    //               </h4>
    //               <div className="space-y-3">
    //                 {[{name:"All"},...categories].map((cat) => (
    //                   <button
    //                     key={cat?.name}
    //                     className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
    //                       selectedCategory === cat?.name
    //                         ? "bg-purple-900 text-white"
    //                         : "text-purple-700 hover:bg-purple-50"
    //                     }`}
    //                     onClick={() =>
    //                       setSelectedCategory(
    //                         selectedCategory === cat?.name ? "" : cat?.name
    //                       )
    //                     }
    //                   >
    //                     {cat?.name}
    //                   </button>
    //                 ))}
    //               </div>
    //             </div>

    //             {/* Price Range */}
    //             <div>
    //               <h4 className="text-sm font-medium text-purple-600 mb-4">
    //                 Price Range
    //               </h4>
    //               <input
    //                 type="range"
    //                 min="0"
    //                 max="200"
    //                 step="10"
    //                 value={priceRange[1]}
    //                 onChange={(e) =>
    //                   setPriceRange([priceRange[0], parseInt(e.target.value)])
    //                 }
    //                 className="w-full accent-purple-900"
    //               />
    //               <div className="flex justify-between mt-2">
    //                 <span className="text-sm text-purple-600">
    //                   ₹{priceRange[0]}
    //                 </span>
    //                 <span className="text-sm text-purple-600">
    //                   ₹{priceRange[1]}
    //                 </span>
    //               </div>
    //             </div>
    //           </div>
    //         </aside>
    //       )}

    //       {/* Product Grid */}
    //       <div className="flex-1">
    //         {products.length > 0 ? (
    //           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    //             {products.map((product) => (
    //               <div
    //                 key={product.id}
    //                 className="group bg-white rounded-xl overflow-hidden shadow-sm border border-purple-100 hover:shadow-lg transition-all duration-300"
    //                 onClick={() =>
    //                   navigate("/product-details", { state: { product } })
    //                 }
    //               >
    //                 <div className="relative aspect-square overflow-hidden">
    //                   <img
    //                     src={product?.images[0]}
    //                     alt={product.name}
    //                     className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
    //                   />
    //                   <button className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
    //                     <Heart className="w-5 h-5 text-purple-600 hover:text-red-500 transition-colors" />
    //                   </button>
    //                 </div>
    //                 <div className="p-6">
    //                   <h4 className="text-lg font-medium text-purple-900 mb-2">
    //                     {product.name}
    //                   </h4>
    //                   <p className="text-xl font-serif text-purple-800 mb-4">
    //                     ₹{product.price}
    //                   </p>
    //                   <AddToCartButton
    //                     onClick={(e) => {
    //                       addToCart(product);
    //                     }}
    //                   />
    //                 </div>
    //               </div>
    //             ))}
    //           </div>
    //         ) : (
    //           <div className="text-center py-12">
    //             <p className="text-xl text-purple-600">
    //               No products found matching your criteria.
    //             </p>
    //           </div>
    //         )}
    //       </div>
    //     </div>
    //   </div>

    //   <WhatsAppButton />
    //   <Footer />
    // </div>
  );
};

export default SearchPage;
